import { trackEvent } from '@/lib/mixpanel';
import { isDocumentHidden } from '@/lib/utils/page-visibility';

// Analytics for the `GET /deals/:dealId/events` SSE stream lifecycle.
//
// Mirrors the WEBSOCKET_* incident tracking in `ws-analytics.ts` so the two
// real-time transports can be compared side by side (connection stability,
// downtime, reconnections). Unlike the WebSocket client — a long-lived global
// with its own event emitter — each SSE stream is owned by a `useDealEventStream`
// effect, so this is a small per-stream state machine created via
// `createDealSseAnalytics(dealId)` rather than a module-level singleton.

type SseDropCause = 'ended' | 'error';

// `paused`: the stream was closed on purpose because the tab is hidden
// (`useDealEventStream`); `resumed`: the tab is visible again and the stream is
// reopening. Neither is an incident, so the open that follows a resume emits
// nothing — RECONNECTED is reserved for recovery after a DROPPED.
type SseState = 'idle' | 'connected' | 'down' | 'paused' | 'resumed';

function now(): number {
  return Date.now();
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

export type DealSseAnalytics = {
  /** A stream opened. Emits CONNECTED on first open, RECONNECTED after a drop. */
  onConnected: () => void;
  /** A live stream ended (EOF) or failed mid-read. No-op if not connected. */
  onDropped: (cause: SseDropCause) => void;
  /**
   * The stream is being closed on purpose (hidden tab). Emits nothing; closes
   * any drop episode in progress so the hidden time never counts as downtime.
   */
  onPaused: () => void;
  /** The tab is visible again. Emits RESUMED with the time spent paused. */
  onResumed: () => void;
};

export function createDealSseAnalytics(
  dealId: string | null
): DealSseAnalytics {
  let state: SseState = 'idle';
  let connectedAt: number | null = null;
  let disconnectedAt: number | null = null;
  let pausedAt: number | null = null;
  let everConnected = false;
  let reconnectAttempts = 0;

  return {
    onConnected(): void {
      if (state === 'down') {
        reconnectAttempts += 1;
        trackEvent('DEAL_SSE_RECONNECTED', {
          dealId,
          downtimeMs:
            disconnectedAt !== null ? Math.max(0, now() - disconnectedAt) : 0,
          attempts: reconnectAttempts,
        });
      } else if (state === 'idle') {
        trackEvent('DEAL_SSE_CONNECTED', { dealId });
      }
      state = 'connected';
      everConnected = true;
      connectedAt = now();
      disconnectedAt = null;
    },

    onDropped(cause): void {
      if (state !== 'connected') return;
      trackEvent('DEAL_SSE_DROPPED', {
        dealId,
        cause,
        connectionAgeMs:
          connectedAt !== null ? Math.max(0, now() - connectedAt) : 0,
        wasHidden: isDocumentHidden(),
        online: isOnline(),
      });
      state = 'down';
      disconnectedAt = now();
    },

    onPaused(): void {
      if (state === 'paused') return;
      state = 'paused';
      pausedAt = now();
      connectedAt = null;
      disconnectedAt = null;
    },

    onResumed(): void {
      if (state !== 'paused') return;
      trackEvent('DEAL_SSE_RESUMED', {
        dealId,
        hiddenMs: pausedAt !== null ? Math.max(0, now() - pausedAt) : 0,
      });
      pausedAt = null;
      // A stream that never opened before the pause is still a first open.
      state = everConnected ? 'resumed' : 'idle';
    },
  };
}
